import { coworkExecutionContext, executionContextPrefix, type ExecutionHints } from "./execution-context"
import { coworkLanguageInstructions } from "./language-policy"
import { buildCoworkReportTemplatePrompt } from "./report-prompt"
import { coworkAccountingReportTemplates } from "./report-templates"

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value)

// Native admission flattens synthetic parts into text. This is a display-only
// inverse of the current platform envelope, never an input/binding transformer.
export function coworkUserPrompt(text: string): string {
  const language = text.startsWith("Current-turn language settings:\n")
  if (!language && !text.startsWith(executionContextPrefix)) return text
  const offset = language ? text.indexOf("\n" + executionContextPrefix) + 1 : 0
  if (language && offset === 0) return text
  const end = text.indexOf("\n", offset)
  const line = text.slice(offset, end < 0 ? undefined : end)
  try {
    const context: unknown = JSON.parse(line.slice(executionContextPrefix.length))
    if (!record(context) || context.version !== 1 || !record(context.intent) || !record(context.hints)) return text
    const { intent, hints } = context
    if (typeof intent.task !== "string" || typeof intent.text !== "string" ||
      !Array.isArray(intent.requested_outputs) || !intent.requested_outputs.every((item) => typeof item === "string")) return text
    if (Object.entries(hints).some(([key, value]) => key === "selected_file_ids"
      ? !Array.isArray(value) || !value.every((item) => typeof item === "string")
      : typeof value !== "string")) return text
    if (!language && (typeof hints.project_id !== "string" || !hints.project_id)) return text
    const canonical = executionContextPrefix + JSON.stringify(coworkExecutionContext(intent.text, {
      task: intent.task, requested_outputs: intent.requested_outputs, hints: hints as ExecutionHints,
    }))
    // Unknown keys, versions, reordered/noncanonical JSON and changed policy
    // paragraphs are user evidence, not a platform envelope we can remove.
    if (line !== canonical) return text
    let prefix = canonical
    if (language) {
      if (typeof hints.application_locale !== "string" || hints.reply_language !== hints.application_locale ||
        typeof hints.report_output_language !== "string") return text
      prefix = coworkLanguageInstructions({ applicationLocale: hints.application_locale, reportOutputLanguage: hints.report_output_language }) + "\n" + canonical
    }
    const suffix = intent.text.trim() ? "\n" + intent.text : ""
    if (text === prefix + suffix) return intent.text
    if (intent.task !== "generate_report" || !text.startsWith(prefix + "\nreport_template_context: ")) return text
    const templateLine = text.slice(prefix.length + 1).split("\n", 1)[0]!
    const templateContext: unknown = JSON.parse(templateLine.slice("report_template_context: ".length))
    if (!record(templateContext)) return text
    const template = coworkAccountingReportTemplates.find((item) => item.id === templateContext.template_id)
    if (!template) return text
    const selection = record(templateContext.parameters) ? templateContext.parameters.account_card : undefined
    const { instructions } = buildCoworkReportTemplatePrompt(template, (key) => key as string,
      selection as Parameters<typeof buildCoworkReportTemplatePrompt>[2])
    return text === prefix + "\n" + instructions + suffix ? intent.text : text
  } catch {
    return text
  }
}
